import * as yup from "yup";
import { pathSchema } from "features/Path/schema";
import { PreferredTimeSlot } from "features/Resources/Meeting/models";
import { meetingTemplateTimingSchema } from "./Components/Form/Timing/ReferenceEventTimingForm/schema";
import { MeetingReferenceEvent } from "./Components/Form/Timing/models";
import { MeetingTemplateType, OffsetUnit, TemplateMeetingAttendee } from "./models";

const isRecurring = (meetingType: string) => meetingType === "group_session_recurring";

export const meetingTemplateSchema = (isNewImport: boolean = false) => meetingTemplateTimingSchema.shape({
  pathIds: yup.array().of(yup.string().required()).optional().default([]),
  paths: yup.array().of(pathSchema).optional().default([]),
  name: yup.string().nullable().required().label("Title").max(70, "Title cannot be more than 70 characters"),
  meetingType: yup
    .mixed<MeetingTemplateType>()
    .required("Meeting Type must be either Individual or Group Session (recurring or ad-hoc)")
    .label("Meeting Type"),

  learningObjective: yup
    .string()
    .nullable()
    .when("meetingType", {
      is: (v: string) => ["group_session_adhoc", "group_session_recurring"].includes(v),
      then: (schema) => (isNewImport ? schema.optional() : schema.required("Learning Objective is required for group sessions")),
      otherwise: (schema) => schema.optional(),
    })
    .label("Learning Objective"),
  purpose: yup
    .string()
    .nullable()
    .when("meetingType", {
      is: "individual",
      then: (schema) => schema.required("Meeting Purpose is required for individual meetings"),
      otherwise: (schema) => schema.optional(),
    })
    .label("Purpose"),
  additionalInfo: yup.string().nullable().optional().label("Additional Info"),
  attendees: yup.array().of(yup.mixed<TemplateMeetingAttendee>().defined()).optional().label("Attendees"),
  leadCompositeId: yup.string().nullable()
    .when("meetingType", {
      is: (v: string) => ["group_session_adhoc", "group_session_recurring"].includes(v),
      then: (schema) => (isNewImport ? schema.optional() : schema.required("Lead is required for group sessions")),
      otherwise: (schema) => schema.optional(),
    }),
  // Reference-event timing (individual / ad-hoc). Recurring meetings schedule via
  // seriesStartsAt/recurrenceRules/timezone instead, so these must relax for them.
  referenceEvent: yup
    .mixed<MeetingReferenceEvent>()
    .nullable()
    .when("meetingType", {
      is: isRecurring,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required().label("Event"),
    }),
  offset: yup.number().optional(),
  offsetUnit: yup
    .mixed<OffsetUnit>()
    .label("Day/Week/Month")
    .when(["meetingType", "offset"], {
      // A missing offset is a same-day meeting — no unit needed.
      is: (meetingType: string, offset: number) => !isRecurring(meetingType) && (offset ?? 0) !== 0,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    }),
  specificTimeOfDay: yup
    .string()
    .nullable()
    .when(["meetingType", "useSpecificTime"], {
      is: (meetingType: string, useSpecificTime: boolean) => !isRecurring(meetingType) && useSpecificTime === true,
      then: (schema) => schema.required("Please select a specific time").matches(/^\d{2}:\d{2}$/, "Time must be in HH:MM format"),
      otherwise: (schema) => schema.notRequired(),
    }),
  preferredTimeSlot: yup
    .mixed<PreferredTimeSlot>()
    .nullable()
    .when(["meetingType", "useSpecificTime"], {
      is: (meetingType: string, useSpecificTime: boolean) => !isRecurring(meetingType) && useSpecificTime === false,
      then: (schema) => schema.required().label("Time of Day"),
      otherwise: (schema) => schema.notRequired(),
    }),
  durationMinutes: yup.number()
    .required("Duration is required")
    .min(1, "Duration must be at least 1 minute")
    .max(480, "Duration cannot exceed 8 hours"),
  allowWeekends: yup.boolean().optional().default(false).label("Allow Weekends"),
});

export type MeetingTemplateSchema = yup.InferType<ReturnType<typeof meetingTemplateSchema>>;

/**
 * NOTE: This schema type may include form-specific fields (like 'paths') that aren't sent to the API.
 * Use toCreateMeetingTemplatePayload() or toUpdateMeetingTemplatePayload() from ./helpers
 * to transform form data into the correct API payload structure.
 */
